<template>
  <div class="wrapper-table-import">
    <!-- Error Message -->
    <section v-if="mapErrorMessages.length">
      <div class="row">
        <div class="col-12 mt-2 mb-1">
          <p
            v-for="(error, index) in mapErrorMessages"
            :key="index"
            class="text-danger mb-2"
          >
            <span v-html="`${error.message} (${error.count})`"/>
          </p>
        </div>
      </div>
    </section>

    <!-- Table -->
    <section>
      <div class="row">
        <div class="col-12 wrapper-table">
          <table class="table table-bordered m-0">
            <thead>
              <tr>
                <td
                  v-for="(column, columnIndex) in columns"
                  :key="columnIndex"
                >
                  <div class="dropdown item">
                    <button
                      :disabled="isDisabledSelectColumn(column.key, columnIndex)"
                      :class="[`button-${columnIndex}`]"
                      class="btn no-bg text-secondary"
                      type="button"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      {{ column.name }}
                      {{ column.required ? '*' : '' }}
                      <span class="icon-import">
                        <i class="far fa-arrow-alt-circle-down"/>
                        <i
                          v-if="column.required && rows[column.key] == 0 && !isDisabledSelectColumn(column.key, columnIndex)"
                          class="far fa-hand-point-down text-warning vert-move"
                        />
                      </span>
                    </button>
                    <div class="dropdown-menu">
                      <form
                        class="px-3 py-2"
                        style="min-width: 300px;">
                        <template v-if="rows[column.key] == 0">
                          <label style="font-size: 11px;">
                            {{ $t('message.choseyourcolumn', {string: column.name}) }}
                          </label>
                          <select
                            class="form-control custom-select"
                            @change="handleSelectColumn($event, column.key, columnIndex)"
                          >
                            <option value="">{{ $t('placeholder.select') }}</option>
                            <option
                              v-for="(columnName, index) in uploadedHeadColumnNames"
                              :key="index"
                              :value="index"
                            >
                              {{ columnName }}
                            </option>
                          </select>
                        </template>
                        <button
                          v-else
                          type="button"
                          class="btn btn-light btn-sm btn-block"
                          @click="handleUnselectColumn(column.key, columnIndex)">
                          {{ $t('message.unselectedcolumn') }}
                        </button>
                      </form>
                    </div>
                  </div>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr v-if="maxRowNumber == 0">
                <td
                  :colspan="columns.length"
                  class="text-center not-found"
                >
                  {{ $t('message.needtoimportdata') }}
                </td>
              </tr>
              <tr
                v-for="(rowItem, rowIndex) in mapRows"
                v-else
                :key="rowIndex"
                style="min-height: 35px;"
              >
                <td
                  v-for="(columnItem, columnIndex) in rowItem.columns"
                  :key="columnIndex"
                  :class="[
                    {
                      'is-invalid': columnItem.errorMessage,
                    }
                  ]"
                >
                  <span v-if="columnItem.rowsSize > 0">
                    {{ columnItem.value || '&nbsp;' }}
                  </span>
                  <span v-if="columnItem.errorMessage">
                    <vs-tooltip
                      :text="columnItem.errorMessage"
                      position="top"
                    />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section>
      <div
        v-if="maxRowNumber > 0"
        class="row mt-3"
      >
        <div class="col-12">
          <p class="m-0">
            {{ $t('message.importdatacount', {number: maxRowNumber}) }}
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    columns: {
      type: Array,
      default: () => ([]),
    },
    rows: {
      type: Object,
      default: () => ({}),
    },
    uploadedHeadColumnNames: {
      type: Array,
      default: () => ([]),
    },
    autoPickColumn: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {

    }
  },

  computed: {
    columnNames() {
      return this.columns.map((column) => column.name)
    },
    columnKeys() {
      return this.columns.map((column) => column.key)
    },
    maxRowNumber() {
      return Object.keys(this.rows).reduce((max, rowKey) => {
        const rowSize = this.rows[rowKey].length
        if (rowSize > max) {
          max = rowSize
        }
        return max
      }, 0)
    },
    mapRows() {
      const resultRows = []
      for (let rowIndex = 0; rowIndex < this.maxRowNumber; rowIndex++) {
        const resultColumns = []
        for (const columnIndex in this.columnKeys) {
          const columnKey = this.columnKeys[columnIndex]
          const columnName = this.columnNames[columnIndex]
          const columnTxt = this.rows[columnKey][rowIndex]
          const rowsSize = this.rows[columnKey].length
          const columnErrMsg = this.columns[columnIndex].errorMessage(
            this.rows[columnKey][rowIndex],
            columnName,
            this.columns[columnIndex].required,
            rowsSize,
            this.rows,
            rowIndex,
          )
          resultColumns.push({
            name: columnName,
            value: columnTxt,
            errorMessage: columnErrMsg,
            rowsSize: rowsSize,
          })
        }
        resultRows.push({
          columns: resultColumns,
        })
      }
      return resultRows
    },
    mapErrorMessages() {
      const array = this.mapRows.reduce((acc, cur) => {
        cur.columns.forEach(column => {
          if (column.errorMessage) {
            acc.push(column.errorMessage)
          }
        })
        return acc
      }, [])
      const object = array.reduce((acc, cur) => {
        if (acc[cur]) {
          acc[cur]++
        } else {
          acc[cur] = 1
        }
        return acc
      }, {})
      return Object.keys(object).reduce((acc, key) => {
        acc.push({
          message: key,
          count: object[key],
        })
        return acc
      }, [])
    },
    requiredColumnNames() {
      return this.columns
        .filter((col) => col.required == true)
        .map((col) => col.name)
    },
  },

  watch: {
    mapErrorMessages: {
      handler: function() {
        const hasErrMsgs = this.mapErrorMessages.length > 0
        const hasRequiredField = this.getHasMissingRequiredColumn()
        const invalid = hasErrMsgs || hasRequiredField
        this.$emit('update:invalid', invalid)
      },
      immediate: true,
    },
  },

  mounted() {
    if (this.autoPickColumn) {
      this.columns.forEach((column, columnIndex) => {
        const event = {
          target: {
            value: columnIndex,
          },
        }
        const isToggleDropdown = false
        this.handleSelectColumn(event, column.key, columnIndex, isToggleDropdown)
      })
    }
  },

  methods: {
    getHasMissingRequiredColumn() {
      let hasMissingRequiredColumn = false
      this.requiredColumnNames.forEach((columnName, index) => {
        const column = this.columns.find((col) => col.name === columnName)
        if (this.rows[column.key] == 0) {
          hasMissingRequiredColumn = true
        }
      })
      return hasMissingRequiredColumn
    },
    handleSelectColumn(event, staticColumnKey, staticColumnIndex, isToggleDropdown = true) {
      if (isToggleDropdown) {
        this.closeDropdownColumn(staticColumnIndex)
      }

      const uploadColumnIndex = Number(event.target.value)
      this.$emit('onSelectColumn', staticColumnKey, uploadColumnIndex, this.maxRowNumber)
    },
    handleUnselectColumn(columnKey, columnIndex) {
      this.closeDropdownColumn(columnIndex)
      this.$emit('onUnselectColumn', columnKey, columnIndex, this.maxRowNumber)
    },
    closeDropdownColumn(columnIndex) {
      $(`.dropdown button.button-${columnIndex}`).dropdown('toggle')
    },
    isDisabledSelectColumn(columnKey, columnIndex) {
      if (columnIndex === 0) {
        return false
      }
      return this.rows[this.columns[0].key] == 0
    },
  },
}
</script>

<style scoped>
.table thead tr td {
  position: relative;
  padding: 0;
}
.table thead tr .fa-hand-point-down {
  position: absolute;
  top: -12px;
  left: 1px;
}
.table thead tr td .icon-import {
  position: relative;
}
.table thead tr td .dropdown button[data-toggle="dropdown"] {
  width: 100%;
  text-align: left;
  padding: 10px;
}
.table thead tr td .dropdown .dropdown-menu {
  top: -1px !important;
  left: -1px !important;
}
.table thead tr td .dropdown .dropdown-menu form {
  font-size: 14px;
}
.table tbody tr td.not-found {
  color: #ccc;
}

.is-invalid {
  background-color: var(--danger);
}

tbody tr td {
  position: relative;
}

tbody tr td .con-vs-tooltip {
  display: none;
}

tbody tr td.is-invalid .con-vs-tooltip {
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
}

.vert-move {
    -webkit-animation: mover 0.5s infinite  alternate;
    animation: mover 0.5s infinite  alternate;
}
.vert-move {
    -webkit-animation: mover 0.5s infinite  alternate;
    animation: mover 0.5s infinite  alternate;
}
@-webkit-keyframes mover {
    0% { transform: translateY(0); }
    100% { transform: translateY(-10px); }
}
@keyframes mover {
    0% { transform: translateY(0); }
    100% { transform: translateY(-10px); }
}
.wrapper-table {
  overflow: auto;
  min-height: 150px;
  max-height: 390px;
  padding-top: 12px;
  margin-top: -8px;
}
.wrapper-table .btn:disabled {
  opacity: .3;
}
</style>



// WEBPACK FOOTER //
// src/components/base/BaseTableImportExcel.vue